// frontend/src/api/images.js
import apiClient from './client'; // Your configured axios instance

const logger = console; // Basic logger for API calls

/**
 * Fetches images based on provided filter parameters.
 * @param {object} params - Query parameters for filtering images.
 * e.g., { skip, limit, status, submitted, staged, archived, user_id_filter, commodity, sub_commodity, created_after, created_before, gtin_search }
 * @returns {Promise<Array>} A promise that resolves to an array of image objects.
 */
export const getUserImages = async (params = {}) => {
  try {
    logger.info("API Call: getUserImages, Params:", params);
    const response = await apiClient.get('/images/list', { params });
    // Return just the items array for backward compatibility
    if (response.data && response.data.items !== undefined) {
      return response.data.items;
    }
    return response.data || [];
  } catch (error) {
    logger.error("Error fetching user images:", error.response?.data || error.message, error);
    throw error.response?.data || new Error("Failed to fetch images");
  }
};

/**
 * Fetches images with pagination info
 * @returns {Promise<{items: Array, total: number}>} A promise that resolves to paginated response.
 */
export const getUserImagesWithPagination = async (params = {}) => {
  try {
    logger.info("API Call: getUserImagesWithPagination, Params:", params);
    const response = await apiClient.get('/images/list', { params });
    // Return the full response object to support pagination
    if (response.data && response.data.items !== undefined) {
      return response.data; // Return full object {items, total}
    }
    // Fallback for old endpoint - wrap array in object
    return Array.isArray(response.data) ? { items: response.data, total: response.data.length } : { items: [], total: 0 };
  } catch (error) {
    logger.error("Error fetching user images with pagination:", error.response?.data || error.message, error);
    throw error.response?.data || new Error("Failed to fetch images");
  }
};

/**
 * Fetches list of users for filtering.
 * @returns {Promise<Array>} A promise that resolves to an array of user objects.
 */
export const getUsers = async () => {
  try {
    const response = await apiClient.get('/users/list');
    return response.data || [];
  } catch (error) {
    logger.error("Error fetching users:", error);
    throw error;
  }
};

/**
 * Fetches unique commodity and sub-commodity values for filtering.
 * @returns {Promise<{commodities: Array<string>, sub_commodities: Array<string>}>} A promise that resolves to commodity values.
 */
export const getCommodityValues = async () => {
  try {
    logger.info("API Call: getCommodityValues");
    const response = await apiClient.get('/images/commodities');
    return response.data;
  } catch (error) {
    logger.error("Error fetching commodity values:", error.response?.data || error.message, error);
    throw error.response?.data || new Error("Failed to fetch commodity values");
  }
};

/**
 * Fetches details for a single image.
 * @param {number} imageId - The ID of the image.
 * @returns {Promise<object>} A promise that resolves to the image object.
 */
export const getImageDetails = async (imageId) => {
  try {
    logger.info(`API Call: getImageDetails, ID: ${imageId}`);
    const response = await apiClient.get(`/images/${imageId}`);
    return response.data;
  } catch (error) {
    logger.error(`Error fetching image details for ID ${imageId}:`, error.response?.data || error.message, error);
    throw error.response?.data || new Error("Failed to fetch image details");
  }
};

/**
 * Creates a single MRHI image.
 * @param {object} gtinPayload - Payload containing the GTIN, e.g., { gtin: "..." }.
 * @returns {Promise<object>} A promise that resolves to the created image object.
 */
export const auditDuplicateImages = async () => {
  const response = await apiClient.get('/images/audit-duplicates');
  return response.data;
};

export const mergeDuplicateImages = async (gtins = null) => {
  const response = await apiClient.post('/images/merge-duplicates', { gtins });
  return response.data;
};

export const checkDuplicateGtins = async (gtins) => {
  try {
    const response = await apiClient.post('/images/check-duplicates', { gtins });
    return response.data;
  } catch (error) {
    logger.error("Error checking duplicate GTINs:", error.response?.data || error.message);
    throw error.response?.data || new Error("Failed to check for duplicates");
  }
};

export const createSingleImage = async (gtinPayload) => {
  try {
    logger.info("API Call: createSingleImage, Payload:", gtinPayload);
    const response = await apiClient.post('/images/single', gtinPayload);
    return response.data;
  } catch (error) {
    logger.error("Error creating single image:", error.response?.data || error.message, error);
    throw error.response?.data || new Error("Failed to create image");
  }
};

/**
 * Initiates asynchronous batch image creation.
 * @param {object} payload - Payload containing a list of GTINs, e.g., { gtins: ["...", "..."] }.
 * @returns {Promise<object>} A promise that resolves to the async batch start response.
 */
export const createBatchImagesAsync = async (payload) => {
  try {
    logger.info("API Call: createBatchImagesAsync, Payload:", payload);
    const response = await apiClient.post('/images/batch/async', payload);
    return response.data;
  } catch (error) {
    logger.error("Error creating batch images asynchronously:", error.response?.data || error.message, error);
    throw error.response?.data || new Error("Failed to start asynchronous batch image creation");
  }
};

/**
 * Uploads a file of GTINs for asynchronous batch processing.
 * @param {FormData} formData - FormData object containing the file.
 * @returns {Promise<object>} A promise that resolves to the async batch start response.
 */
export const uploadGtinListForAsyncProcessing = async (formData) => {
  try {
    logger.info("API Call: uploadGtinListForAsyncProcessing");
    const response = await apiClient.post('/images/upload-gtin-list', formData, {
      headers: {
        'Content-Type': 'multipart/form-data',
      },
    });
    return response.data;
  } catch (error) { // Corrected: Added opening curly brace
    logger.error("Error uploading GTIN list:", error.response?.data || error.message, error);
    throw error.response?.data || new Error("Failed to upload GTIN list");
  }
};


/**
 * Submits a batch of (staged) images to Syndigo.
 * @param {object} payload - Payload containing image IDs, e.g., { image_ids: [1, 2, 3] }.
 * @returns {Promise<Array>} A promise that resolves to a list of updated image objects.
 */
export const submitBatchImagesToSyndigo = async (payload) => {
  try {
    logger.info("API Call: submitBatchImagesToSyndigo, Payload:", payload);
    logger.info("Payload type:", typeof payload);
    logger.info("Payload keys:", Object.keys(payload));
    logger.info("image_ids value:", payload.image_ids);
    logger.info("image_ids type:", typeof payload.image_ids);
    logger.info("Is array:", Array.isArray(payload.image_ids));
    
    // Ensure we're sending the correct format
    const requestData = {
      image_ids: Array.isArray(payload.image_ids) ? payload.image_ids : [],
      publish_after_submit: !!payload.publish_after_submit,
    };
    // Edited Syndigo attributes (single-image submit); only sent when the user changed something.
    if (payload.product_attribute_updates) {
      requestData.product_attribute_updates = payload.product_attribute_updates;
    }
    logger.info("Sending request with data:", JSON.stringify(requestData));

    const response = await apiClient.post('/images/batch/submit', requestData);
    return response.data;
  } catch (error) {
    logger.error("Error submitting batch images to Syndigo:", error.response?.data || error.message, error);
    logger.error("Error response status:", error.response?.status);
    logger.error("Error response headers:", error.response?.headers);
    
    // Log validation error details if present
    if (error.response?.data?.detail) {
      logger.error("Validation error details:", error.response.data.detail);
    }
    
    throw error.response?.data || new Error("Failed to submit images to Syndigo");
  }
};

/**
 * Stages a batch of images, marking them as ready for submission.
 * @param {object} payload - Payload containing image IDs, e.g., { image_ids: [1, 2, 3] }.
 * @returns {Promise<Array>} A promise that resolves to a list of updated image objects.
 */
export const stageBatchImages = async (payload) => {
  try {
    logger.info("API Call: stageBatchImages, Payload:", payload);
    const response = await apiClient.post('/images/stage', payload);
    return response.data;
  } catch (error) {
    logger.error("Error staging images:", error.response?.data || error.message, error);
    throw error.response?.data || new Error("Failed to stage images");
  }
};

/**
 * Unstages a batch of images, moving them back from "Staged" to "Preview".
 * @param {object} payload - Payload containing image IDs, e.g., { image_ids: [1, 2, 3] }.
 * @returns {Promise<Array>} A promise that resolves to a list of updated image objects.
 */
export const unstageBatchImages = async (payload) => {
  try {
    logger.info("API Call: unstageBatchImages, Payload:", payload);
    const response = await apiClient.post('/images/unstage', payload);
    return response.data;
  } catch (error) {
    logger.error("Error unstaging images:", error.response?.data || error.message, error);
    throw error.response?.data || new Error("Failed to unstage images");
  }
};

/**
 * Archives (soft deletes) a batch of images.
 * @param {object} payload - Payload containing image IDs, e.g., { image_ids: [1, 2, 3] }.
 * @returns {Promise<Array>} A promise that resolves to a list of updated image objects.
 */
export const archiveBatchImages = async (payload) => {
  try {
    logger.info("API Call: archiveBatchImages, Payload:", payload);
    const response = await apiClient.post('/images/archive', payload);
    return response.data;
  } catch (error) {
    logger.error("Error archiving images:", error.response?.data || error.message, error);
    throw error.response?.data || new Error("Failed to archive images");
  }
};

/**
 * Restores a batch of archived images (moves them to "Preview").
 * @param {object} payload - Payload containing image IDs, e.g., { image_ids: [1, 2, 3] }.
 * @returns {Promise<Array>} A promise that resolves to a list of updated image objects.
 */
export const unarchiveBatchImages = async (payload) => {
  try {
    logger.info("API Call: unarchiveBatchImages, Payload:", payload);
    const response = await apiClient.post('/images/restore', payload);
    return response.data;
  } catch (error) {
    logger.error("Error restoring images:", error.response?.data || error.message, error);
    throw error.response?.data || new Error("Failed to restore images");
  }
};

/**
 * Retries submission for a single image that previously failed to submit.
 * @param {number} imageId - The ID of the image to retry submission for.
 * @returns {Promise<object>} A promise that resolves to the updated image object.
 */
export const retrySubmitImage = async (imageId, { publishAfterSubmit = false } = {}) => {
  try {
    logger.info(`API Call: retrySubmitImage, ID: ${imageId}, publishAfterSubmit: ${publishAfterSubmit}`);
    const response = await apiClient.post(
      `/images/${imageId}/retry-submit`,
      null,
      { params: { publish_after_submit: publishAfterSubmit } },
    );
    return response.data;
  } catch (error) {
    logger.error(`Error retrying submission for image ${imageId}:`, error.response?.data || error.message, error);
    throw error.response?.data || new Error("Failed to retry submission");
  }
};

/**
 * Regenerates an image with specified overrides.
 * @param {number} imageId - The ID of the image to regenerate.
 * @param {object} overrides - An object containing the override values.
 * @returns {Promise<object>} A promise that resolves to the regenerated image object.
 */
export const regenerateImage = async (imageId, overrides) => {
  try {
    logger.info(`API Call: regenerateImage, ID: ${imageId}, Overrides:`, overrides);
    const response = await apiClient.patch(`/images/${imageId}/regenerate`, overrides);
    return response.data;
  } catch (error) {
    logger.error(`Error regenerating image ${imageId}:`, error.response?.data || error.message, error);
    throw error.response?.data || new Error("Failed to regenerate image");
  }
};

// --- Functions from your original images.js to ensure compatibility ---
// (May overlap with some above, adjust as needed)

// Generate batch MRHIs (Synchronous version if still needed)
export const generateBatchMrhis = async (gtins) => {
  try {
    const response = await apiClient.post('/images/batch', { gtins }); // Assumes backend has this endpoint
    return response.data;
  } catch (error) {
    logger.error("Error in generateBatchMrhis:", error.response?.data || error.message, error);
    throw error;
  }
};

// Get image file (as binary data)
export const getImageFile = async (imageId) => {
  try {
    logger.info(`Fetching image file for ID: ${imageId}`);
    const timestamp = new Date().getTime();
    // Using apiClient which should handle base URL and auth headers
    const response = await apiClient.get(`/images/${imageId}/file?t=${timestamp}`, {
      responseType: 'blob',
      headers: {
        'Cache-Control': 'no-cache, no-store, must-revalidate',
        'Pragma': 'no-cache',
        'Expires': '0'
      }
    });
    return response.data;
  } catch (error) {
    logger.error('Error in getImageFile:', error.response?.data || error.message, error);
    throw error;
  }
};

// Get image as base64
export const getImageBase64 = async (imageId) => {
  try {
    const response = await apiClient.get(`/images/${imageId}/base64`);
    // Assuming backend response is { base64_data: "..." } as per schema
    return response.data.base64_data; 
  } catch (error) {
    logger.error("Error in getImageBase64:", error.response?.data || error.message, error);
    throw error;
  }
};

// Submit single image (if different from batch submit logic)
export const submitImage = async (imageId, { publishAfterSubmit = false } = {}) => {
  try {
    const response = await apiClient.post(
      `/images/${imageId}/submit`,
      null,
      { params: { publish_after_submit: publishAfterSubmit } },
    );
    return response.data;
  } catch (error) {
    logger.error("Error submitting single image:", error.response?.data || error.message, error);
    throw error;
  }
};

// Fetch product title, description and feature bullets from Syndigo for the submit dialog
export const getImageProductDetails = async (imageId) => {
  try {
    const response = await apiClient.get(`/images/${imageId}/product-details`);
    return response.data;
  } catch (error) {
    logger.error("Error fetching product details:", error.response?.data || error.message, error);
    throw error.response?.data || new Error("Failed to fetch product details");
  }
};



/**
 * Permanently marks images as deleted (soft delete).
 * @param {object} payload - Payload containing image IDs, e.g., { image_ids: [1, 2, 3] }.
 * @returns {Promise<Array>} A promise that resolves to a list of updated image objects.
 */
/**
 * Assigns commodity/sub_commodity to a batch of images.
 * @param {object} payload - { image_ids: [...], commodity: "...", sub_commodity: "..." }
 * @returns {Promise<Array>} Updated image objects.
 */
export const assignCommodityBatch = async (payload) => {
  try {
    logger.info("API Call: assignCommodityBatch, Payload:", payload);
    const response = await apiClient.post('/images/assign-commodity', payload);
    return response.data;
  } catch (error) {
    logger.error("Error assigning commodity:", error.response?.data || error.message, error);
    throw error.response?.data || new Error("Failed to assign commodity");
  }
};

export const deleteBatchImages = async (payload) => {
  try {
    logger.info("API Call: deleteBatchImages, Payload:", payload);
    const response = await apiClient.post('/images/delete-permanently', payload);
    return response.data;
  } catch (error) {
    logger.error("Error deleting images:", error.response?.data || error.message, error);
    throw error.response?.data || new Error("Failed to delete images");
  }
};

export default {
  getUserImages,
  getImageDetails,
  createSingleImage,
  generateBatchMrhis,    // Kept for potential synchronous batch
  createBatchImagesAsync,
  uploadGtinListForAsyncProcessing,
  submitBatchImagesToSyndigo,
  getImageProductDetails,
  stageBatchImages,
  unstageBatchImages,
  archiveBatchImages,
  unarchiveBatchImages,
  assignCommodityBatch,  // Assign commodity to batch
  deleteBatchImages,     // Added delete function
  retrySubmitImage,
  regenerateImage,
  getImageFile,          // Added back
  getImageBase64,        // Added back
  submitImage,           // Added back (for single image submission)
};
